import { useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Table, type TableColumn } from '../components/ui/Table';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import type {
  AcknowledgeAlarmResponse,
  AlarmDto,
  AlarmSeverity,
  ListPlantsResponse,
} from '../api/types';
import { activeAlarmsSignal } from '../realtime/alarmsStore';
import './AlarmsPage.css';

type SeverityFilter = 'All' | AlarmSeverity;

/**
 * Live alarms page. Reads directly from `activeAlarmsSignal` — no separate
 * fetch here, the store already seeded it via `bootstrapAlarms()` and the
 * SignalR channel keeps it fresh.
 *
 * Client-side filter row (severity + plant). Filter state is local because
 * it is UI-only; the source data lives in the module signal.
 *
 * Acknowledge button: fires `POST /api/alarms/{id}/acknowledge`. On 200 the
 * hub broadcasts the resulting `acknowledged` event and the store updates
 * itself — we deliberately do NOT optimistically mutate the row
 * (concept C.6: SignalR broadcast is authoritative).
 */
interface Props { id?: string }

export function AlarmsPage({ id: plantIdFromRoute }: Props = {}) {
  // Plant list — for the plant filter dropdown + the "Anlage" column name.
  const plantsState = useAsync(
    (signal) => api.get<ListPlantsResponse>('/api/plants', signal),
    [],
  );

  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('All');
  // Wenn die Route eine plantId trägt (/plants/:id/alarms), pinnen wir den
  // Filter darauf und blenden das Dropdown aus — der Techniker arbeitet in
  // GENAU dieser Anlage.
  const [plantFilter, setPlantFilter] = useState<string>(plantIdFromRoute ?? 'All');
  const lockedToPlant = !!plantIdFromRoute;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const plantNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (plantsState.status === 'ok') {
      for (const p of plantsState.data.plants) map.set(p.id, p.displayName);
    }
    return map;
  }, [plantsState]);

  const rows = useMemo(() => {
    const all = activeAlarmsSignal.value;
    // Exclude Cleared rows on the active page; those belong to /alarms/history.
    let filtered = all.filter((a) => a.state !== 'Cleared');
    // Hide alarms whose plantId no longer resolves (stale seed leftovers).
    // Skip this filter until the plant list has loaded, otherwise we'd blank
    // the whole table for the first tick after mount.
    if (plantsState.status === 'ok') {
      filtered = filtered.filter((a) => plantNameById.has(a.plantId));
    }
    if (severityFilter !== 'All') {
      filtered = filtered.filter((a) => a.severity === severityFilter);
    }
    if (plantFilter !== 'All') {
      filtered = filtered.filter((a) => a.plantId === plantFilter);
    }
    // Newest first.
    return filtered.slice().sort((a, b) => (a.triggeredAt < b.triggeredAt ? 1 : -1));
  }, [activeAlarmsSignal.value, severityFilter, plantFilter, plantsState, plantNameById]);

  // Only surface plants that currently exist in /api/plants. Alarms whose
  // plantId no longer resolves (leftover rows from earlier seed generations)
  // must not leak orphaned UUIDs into the filter dropdown.
  const filterPlantIds = useMemo(() => {
    const seen = new Set<string>();
    for (const a of activeAlarmsSignal.value) {
      if (plantNameById.has(a.plantId)) seen.add(a.plantId);
    }
    return Array.from(seen).sort((a, b) =>
      (plantNameById.get(a) ?? a).localeCompare(plantNameById.get(b) ?? b),
    );
  }, [activeAlarmsSignal.value, plantNameById]);

  async function acknowledge(alarm: AlarmDto) {
    setBusyId(alarm.id);
    setError(null);
    try {
      await api.post<AcknowledgeAlarmResponse>(`/api/alarms/${alarm.id}/acknowledge`);
      // No mutation here — the SignalR `acknowledged` event will update the
      // row via alarmsStore. If for some reason the broadcast never arrives
      // the row stays "Active" in the UI, which is safer than the reverse.
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setError('Keine Berechtigung, diesen Alarm zu quittieren.');
      } else if (e instanceof ApiError && e.status === 404) {
        setError('Alarm nicht mehr vorhanden.');
      } else {
        setError('Quittierung fehlgeschlagen.');
      }
    } finally {
      setBusyId(null);
    }
  }

  const allColumns: TableColumn<AlarmDto>[] = [
    {
      key: 'severity',
      header: 'Schwere',
      width: '110px',
      render: (a) => (
        <StatusBadge tone={a.severity === 'Alarm' ? 'error' : 'warn'}>
          {a.severity === 'Alarm' ? 'Alarm' : 'Warnung'}
        </StatusBadge>
      ),
    },
    {
      key: 'origin',
      header: 'Herkunft',
      width: '110px',
      render: (a) => (
        <StatusBadge tone={a.origin === 'Device' ? 'info' : 'neutral'}>
          {a.origin === 'Device' ? 'Gerät' : 'Abgeleitet'}
        </StatusBadge>
      ),
    },
    {
      key: 'plant',
      header: 'Anlage',
      render: (a) => plantNameById.get(a.plantId) ?? <code class="mono muted">{a.plantId}</code>,
    },
    {
      key: 'message',
      header: 'Datenpunkt / Meldung',
      render: (a) => <span class="hv-alarms__msg">{a.message}</span>,
    },
    {
      key: 'time',
      header: 'Zeitpunkt',
      width: '160px',
      render: (a) => <span class="mono muted">{formatTimestamp(a.triggeredAt)}</span>,
    },
    {
      key: 'value',
      header: 'Wert / Schwelle',
      width: '160px',
      render: (a) =>
        // Device-origin alarms carry no threshold values; the message is the
        // whole story. Escalated alarms show the classic value/threshold pair.
        a.origin === 'Device' || a.triggeredValue == null || a.thresholdValue == null ? (
          <span class="muted">—</span>
        ) : (
          <span class="mono">
            {formatNumber(a.triggeredValue)}{' '}
            <span class="muted">
              {a.direction === 'High' ? '≥' : '≤'} {formatNumber(a.thresholdValue)}
            </span>
          </span>
        ),
    },
    {
      key: 'state',
      header: 'Status',
      width: '130px',
      render: (a) => (
        <StatusBadge tone={a.state === 'Active' ? 'warn' : 'neutral'}>
          {a.state === 'Active' ? 'Aktiv' : 'Quittiert'}
        </StatusBadge>
      ),
    },
    {
      key: 'action',
      header: '',
      width: '140px',
      render: (a) =>
        a.state === 'Active' ? (
          // Native <button> — the shared `Button` component's Props alias
          // doesn't surface `disabled`. Same workaround as in DashboardPage.
          <button
            type="button"
            class={`hv-btn hv-btn--secondary hv-btn--sm${busyId === a.id ? ' hv-btn--disabled' : ''}`}
            disabled={busyId === a.id}
            onClick={(e) => {
              e.stopPropagation();
              void acknowledge(a);
            }}
          >
            {busyId === a.id ? '…' : 'Quittieren'}
          </button>
        ) : null,
    },
  ];

  // Plant-Spalte weglassen wenn wir plant-scoped sind — sonst zeigt jede
  // Zeile dieselbe Anlage.
  const columns = lockedToPlant
    ? allColumns.filter((c) => c.key !== 'plant')
    : allColumns;
  const plantName = plantIdFromRoute ? (plantNameById.get(plantIdFromRoute) ?? '') : '';

  return (
    <AppShell title={lockedToPlant ? 'Alarme' : 'Alarme (alle Anlagen)'}>
      {lockedToPlant && <PlantSubNav plantId={plantIdFromRoute!} plantName={plantName} />}
      <div class="hv-alarms">
        <div class="hv-alarms__filters">
          <label class="hv-alarms__filter">
            <span>Schwere</span>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter((e.currentTarget as HTMLSelectElement).value as SeverityFilter)}
            >
              <option value="All">Alle</option>
              <option value="Alarm">Alarm</option>
              <option value="Warning">Warnung</option>
            </select>
          </label>
          {!lockedToPlant && (
            <label class="hv-alarms__filter">
              <span>Anlage</span>
              <select
                value={plantFilter}
                onChange={(e) => setPlantFilter((e.currentTarget as HTMLSelectElement).value)}
              >
                <option value="All">Alle</option>
                {filterPlantIds.map((id) => (
                  <option key={id} value={id}>{plantNameById.get(id) ?? id}</option>
                ))}
              </select>
            </label>
          )}
          <div class="hv-alarms__spacer" />
          <a class="hv-alarms__history-link"
             href={lockedToPlant ? `/plants/${plantIdFromRoute}/alarms/history` : '/alarms/history'}>
            Verlauf →
          </a>
        </div>

        {error && <p class="hv-alarms__error">{error}</p>}

        <Table<AlarmDto>
          columns={columns}
          rows={rows}
          rowKey={(a) => a.id}
          emptyLabel="Keine aktiven Alarme."
        />
      </div>
    </AppShell>
  );
}

/** Concept: "dd.MM.yy HH:mm:ss" — compact, unambiguous in German locale. */
function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const yy = pad(d.getFullYear() % 100);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${yy} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  // Two decimals of headroom; strip trailing zeroes so 42 renders as "42".
  return Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, '');
}
